import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { Globe, Plus, Copy, RefreshCw, Trash2, Key, Loader2, Code, ExternalLink, Mail } from "lucide-react";
import { API } from "@/lib/admin-constants";

const SourceManager = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newSourceId, setNewSourceId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newWebsiteUrl, setNewWebsiteUrl] = useState("");
  const [newFromEmail, setNewFromEmail] = useState("");
  const [newFromName, setNewFromName] = useState("");
  const [showSnippet, setShowSnippet] = useState(null);
  const [editingUrl, setEditingUrl] = useState({});
  const [editingSender, setEditingSender] = useState({});

  const fetchSources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sources`, { withCredentials: true });
      setSources(res.data);
    } catch {
      toast.error("Failed to load sources");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const createSource = async () => {
    if (!newSourceId.trim() || !newLabel.trim()) return;
    try {
      const res = await axios.post(`${API}/sources`, {
        source_id: newSourceId.trim().toLowerCase().replace(/\s+/g, "_"),
        label: newLabel.trim(),
        website_url: newWebsiteUrl.trim(),
        from_email: newFromEmail.trim(),
        from_name: newFromName.trim(),
      }, { withCredentials: true });
      toast.success(`Source created! API Key: ${res.data.api_key.slice(0, 12)}...`);
      setShowCreate(false);
      setNewSourceId("");
      setNewLabel("");
      setNewWebsiteUrl("");
      setNewFromEmail("");
      setNewFromName("");
      fetchSources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create source");
    }
  };

  const toggleActive = async (source) => {
    try {
      await axios.put(`${API}/sources/${source.source}`, {
        is_active: !source.is_active,
      }, { withCredentials: true });
      toast.success(`Source ${!source.is_active ? "activated" : "deactivated"}`);
      fetchSources();
    } catch {
      toast.error("Failed to update source");
    }
  };

  const saveWebsiteUrl = async (sourceId, url) => {
    try {
      await axios.put(`${API}/sources/${sourceId}`, {
        website_url: url,
      }, { withCredentials: true });
      toast.success("Website URL updated");
      setEditingUrl({});
      fetchSources();
    } catch {
      toast.error("Failed to update website URL");
    }
  };

  const saveSender = async (sourceId, fromEmail, fromName) => {
    try {
      await axios.put(`${API}/sources/${sourceId}`, {
        from_email: fromEmail,
        from_name: fromName,
      }, { withCredentials: true });
      toast.success("Sender updated");
      setEditingSender({});
      fetchSources();
    } catch {
      toast.error("Failed to update sender");
    }
  };

  const regenerateKey = async (sourceId) => {
    try {
      const res = await axios.post(`${API}/sources/${sourceId}/regenerate-key`, {}, { withCredentials: true });
      toast.success("New API key generated");
      fetchSources();
    } catch {
      toast.error("Failed to regenerate key");
    }
  };

  const deleteSource = async (sourceId) => {
    try {
      await axios.delete(`${API}/sources/${sourceId}`, { withCredentials: true });
      toast.success("Source deleted");
      fetchSources();
    } catch {
      toast.error("Failed to delete source");
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("API key copied to clipboard");
  };

  const getSnippet = (source) => {
    const apiUrl = window.location.origin;
    return `<!-- CRM Lead Capture - ${source.label} -->
<!-- RECOMMENDED: Hosted script (works with caching/security plugins) -->
<!-- Add this single line to Divi Theme Options → Integration → Head or Body -->
<script src="${apiUrl}/api/external/snippet.js?source=${source.source}"></script>

<!-- The hosted script automatically:
  - Detects your Divi contact form fields
  - Sends leads to the CRM on form submit
  - Waits for Divi validation before sending
  - Uses your API key: ${source.key}
-->`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-cfc-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="source-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-cfc-primary">External Sources</h1>
          <p className="text-sm text-cfc-text-muted mt-1">
            Connect external websites to your CRM. Each source gets an API key for lead submissions.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-cfc-primary hover:bg-cfc-primary-hover"
          data-testid="add-source-btn"
        >
          <Plus size={16} className="mr-2" /> Add Source
        </Button>
      </div>

      {/* Built-in source */}
      <Card className="border-cfc-border mb-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#0C2E3C] flex items-center justify-center shrink-0">
              <Globe size={20} className="text-[#C8E64E]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-cfc-primary">Commonwealth FC Website</h3>
              <p className="text-xs text-cfc-text-muted">Built-in source — leads from this site&apos;s registration forms</p>
              <code className="text-[10px] text-cfc-text-muted bg-cfc-cream px-1.5 py-0.5 rounded mt-1 inline-block">commonwealth_fc</code>
            </div>
            <span className="text-[10px] font-bold bg-green-100 text-green-800 px-2 py-1 rounded">ACTIVE</span>
          </div>
        </CardContent>
      </Card>

      {/* External sources */}
      <div className="space-y-3">
        {sources.map((source) => (
          <Card key={source.source} className="border-cfc-border" data-testid={`source-card-${source.source}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-700 flex items-center justify-center shrink-0">
                  <ExternalLink size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading font-bold text-cfc-primary">{source.label}</h3>
                    <Switch
                      checked={source.is_active}
                      onCheckedChange={() => toggleActive(source)}
                      data-testid={`toggle-source-${source.source}`}
                    />
                  </div>
                  <code className="text-[10px] text-cfc-text-muted bg-cfc-cream px-1.5 py-0.5 rounded inline-block mb-2">{source.source}</code>

                  {/* API Key */}
                  <div className="flex items-center gap-2 mt-2">
                    <Key size={12} className="text-cfc-text-muted shrink-0" />
                    <code className="text-xs bg-cfc-cream-alt px-2 py-1 rounded font-mono text-cfc-primary truncate max-w-xs">
                      {source.key?.slice(0, 16)}...
                    </code>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyKey(source.key)} data-testid={`copy-key-${source.source}`}>
                      <Copy size={12} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-amber-600" onClick={() => regenerateKey(source.source)} data-testid={`regen-key-${source.source}`}>
                      <RefreshCw size={12} />
                    </Button>
                  </div>

                  {/* Website URL */}
                  <div className="flex items-center gap-2 mt-2">
                    <Globe size={12} className="text-cfc-text-muted shrink-0" />
                    {editingUrl[source.source] !== undefined ? (
                      <>
                        <Input
                          value={editingUrl[source.source]}
                          onChange={(e) => setEditingUrl({...editingUrl, [source.source]: e.target.value})}
                          className="h-7 text-xs max-w-xs"
                          placeholder="https://example.com"
                          data-testid={`edit-url-input-${source.source}`}
                        />
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-green-600" onClick={() => saveWebsiteUrl(source.source, editingUrl[source.source])}>Save</Button>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setEditingUrl({})}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-cfc-text-muted">
                          {source.website_url || <span className="italic opacity-60">No website URL set</span>}
                        </span>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setEditingUrl({[source.source]: source.website_url || ""})} data-testid={`edit-url-${source.source}`}>
                          Edit
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Sender Email */}
                  <div className="flex items-center gap-2 mt-2">
                    <Mail size={12} className="text-cfc-text-muted shrink-0" />
                    {editingSender[source.source] !== undefined ? (
                      <div className="flex-1 space-y-1">
                        <Input
                          value={editingSender[source.source]?.email || ""}
                          onChange={(e) => setEditingSender({...editingSender, [source.source]: {...editingSender[source.source], email: e.target.value}})}
                          className="h-7 text-xs"
                          placeholder="info@example.com"
                          data-testid={`edit-sender-email-${source.source}`}
                        />
                        <Input
                          value={editingSender[source.source]?.name || ""}
                          onChange={(e) => setEditingSender({...editingSender, [source.source]: {...editingSender[source.source], name: e.target.value}})}
                          className="h-7 text-xs"
                          placeholder="Sender display name"
                          data-testid={`edit-sender-name-${source.source}`}
                        />
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-green-600" onClick={() => saveSender(source.source, editingSender[source.source]?.email || "", editingSender[source.source]?.name || "")}>Save</Button>
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setEditingSender({})}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="text-xs text-cfc-text-muted">
                          {source.from_email ? (
                            <>{source.from_name && <span className="font-medium">{source.from_name}</span>} &lt;{source.from_email}&gt;</>
                          ) : (
                            <span className="italic opacity-60">Using default sender (info@commonwealthfc.org)</span>
                          )}
                        </span>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setEditingSender({[source.source]: {email: source.from_email || "", name: source.from_name || ""}})} data-testid={`edit-sender-${source.source}`}>
                          Edit
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => setShowSnippet(source)} data-testid={`snippet-btn-${source.source}`}>
                      <Code size={12} className="mr-1" /> Integration Snippet
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500 border-red-200" onClick={() => deleteSource(source.source)} data-testid={`delete-source-${source.source}`}>
                      <Trash2 size={12} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {sources.length === 0 && (
          <div className="text-center py-12 text-cfc-text-muted">
            <Globe size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No external sources configured yet.</p>
            <p className="text-xs mt-1">Add a source to connect an external website like your WordPress site.</p>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="create-source-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-cfc-primary">Add External Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Source Label</Label>
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g., Cville United FC"
                data-testid="new-source-label"
              />
            </div>
            <div>
              <Label>Source ID (lowercase, no spaces)</Label>
              <Input
                value={newSourceId}
                onChange={(e) => setNewSourceId(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                placeholder="e.g., cville_united"
                data-testid="new-source-id"
              />
              <p className="text-[11px] text-cfc-text-muted mt-1">This identifies the source in the CRM pipeline and templates</p>
            </div>
            <div>
              <Label>Website URL</Label>
              <Input
                value={newWebsiteUrl}
                onChange={(e) => setNewWebsiteUrl(e.target.value)}
                placeholder="e.g., https://cvilleunitedfc.com"
                data-testid="new-source-website-url"
              />
              <p className="text-[11px] text-cfc-text-muted mt-1">Used in email templates for the &ldquo;Visit Our Website&rdquo; button</p>
            </div>
            <div>
              <Label>Sender Email <span className="text-cfc-text-muted font-normal">(optional)</span></Label>
              <Input
                value={newFromEmail}
                onChange={(e) => setNewFromEmail(e.target.value)}
                placeholder="e.g., info@cvilleunitedfc.com"
                data-testid="new-source-from-email"
              />
              <p className="text-[11px] text-cfc-text-muted mt-1">Must be verified in SendGrid. Leave blank to use default sender.</p>
            </div>
            <div>
              <Label>Sender Display Name <span className="text-cfc-text-muted font-normal">(optional)</span></Label>
              <Input
                value={newFromName}
                onChange={(e) => setNewFromName(e.target.value)}
                placeholder="e.g., Cville United FC"
                data-testid="new-source-from-name"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={createSource}
              disabled={!newSourceId.trim() || !newLabel.trim()}
              className="bg-cfc-primary hover:bg-cfc-primary-hover"
              data-testid="confirm-create-source"
            >
              Create Source
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snippet Dialog */}
      <Dialog open={!!showSnippet} onOpenChange={() => setShowSnippet(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="snippet-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-cfc-primary">
              Integration Snippet — {showSnippet?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-cfc-text-muted mb-4">
              Add this script to your WordPress page (or any HTML page) to send form submissions to the CRM.
              Update <code>#your-form-id</code> and field <code>name</code> attributes to match your form.
            </p>
            <div className="bg-gray-900 text-green-400 p-4 rounded text-xs font-mono whitespace-pre-wrap overflow-x-auto">
              {showSnippet && getSnippet(showSnippet)}
            </div>
            <Button
              className="mt-4 bg-cfc-primary hover:bg-cfc-primary-hover"
              onClick={() => {
                navigator.clipboard.writeText(showSnippet ? getSnippet(showSnippet) : "");
                toast.success("Snippet copied");
              }}
              data-testid="copy-snippet-btn"
            >
              <Copy size={14} className="mr-2" /> Copy Snippet
            </Button>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SourceManager;
